import React from 'react';

// --- Utility Types ---
type AsProp<C extends React.ElementType> = {
  as?: C;
};

type PropsToOmit<C extends React.ElementType, P> = keyof (AsProp<C> & P);

type PolymorphicComponentProps<
  C extends React.ElementType,
  P = {}
> = React.PropsWithChildren<P & AsProp<C>> &
  Omit<React.ComponentPropsWithoutRef<C>, PropsToOmit<C, P>>;

type PolymorphicRef<C extends React.ElementType> = React.ComponentPropsWithRef<C>['ref'];

interface ButtonOwnProps {
  children?: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary' | 'text' | 'ghost' | 'outline';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  // New props for background-aware styling
  onDarkBackground?: boolean;
  onBrandBackground?: boolean;
}

type ButtonProps<C extends React.ElementType> = PolymorphicComponentProps<C, ButtonOwnProps>;

// --- Base Component (non-generic at this point) ---
function ButtonBase(
  props: ButtonProps<any>,
  ref: React.Ref<any>
) {
  const {
    as: Component = 'button',
    children,
    className,
    variant = 'primary',
    size = 'md',
    leftIcon,
    rightIcon,
    onDarkBackground = false,
    onBrandBackground = false,
    ...rest
  } = props;

  const baseStyles =
    'font-bold focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center transition-colors duration-200 rounded-md leading-none';

  const variantStyles = {
    primary: 'bg-brand-orange text-white hover:bg-brand-orange-hover focus:ring-brand-orange disabled:bg-brand-orange/50 disabled:cursor-not-allowed',
    secondary: onDarkBackground || onBrandBackground
      ? 'bg-white text-brand-navy hover:bg-gray-50 focus:ring-white/30 disabled:bg-white/50 disabled:text-brand-navy/50 disabled:cursor-not-allowed'
      : 'bg-brand-navy/10 text-brand-navy border border-brand-navy/20 hover:bg-brand-navy/20 hover:border-brand-navy/30 focus:ring-brand-navy/30 disabled:bg-brand-navy/5 disabled:border-brand-navy/10 disabled:text-brand-navy/50 disabled:cursor-not-allowed dark:bg-dark-brand-navy/10 dark:text-dark-brand-navy dark:border-dark-brand-navy/20 dark:hover:bg-dark-brand-navy/20 dark:hover:bg-dark-brand-navy/30 dark:focus:ring-dark-brand-navy/30 dark:disabled:bg-dark-brand-navy/5 dark:disabled:border-brand-navy/10 dark:disabled:text-dark-brand-navy/50',
    text: onDarkBackground || onBrandBackground
      ? 'text-white hover:bg-white/10 focus:ring-white/30 disabled:text-white/50 disabled:cursor-not-allowed'
      : 'text-brand-navy hover:bg-brand-navy/10 focus:ring-brand-navy/30 disabled:text-text-secondary disabled:cursor-not-allowed dark:text-white dark:hover:bg-white/10 dark:focus:ring-white/30 dark:disabled:text-white/50',
    outline: onDarkBackground || onBrandBackground
      ? 'border-2 border-white text-white hover:bg-white hover:text-brand-navy focus:ring-white disabled:border-white/30 disabled:text-white/50 disabled:cursor-not-allowed'
      : 'border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white focus:ring-brand-navy/30 disabled:border-border-color disabled:text-text-secondary disabled:cursor-not-allowed dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-brand-navy dark:focus:ring-white/30 dark:disabled:border-white/30 dark:disabled:text-white/50',
    ghost: onDarkBackground || onBrandBackground
      ? 'text-white/90 hover:bg-white/10 hover:text-white focus:ring-white/30 disabled:text-white/50 disabled:cursor-not-allowed'
      : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary focus:ring-brand-navy/30 disabled:text-text-secondary/50 disabled:cursor-not-allowed dark:text-white dark:hover:bg-white/10 dark:hover:text-white dark:focus:ring-white/30 dark:disabled:text-white/50',
  };

  const sizeStyles = {
    xs: 'py-1 px-2 text-xs',
    sm: 'py-1.5 px-3 text-sm',
    md: 'py-2 px-4 text-base',
    lg: 'py-2.5 px-5 text-lg',
    xl: 'py-3 px-6 text-xl',
  };

  const iconSizeStyles = {
    xs: 'w-4 h-4',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-7 h-7',
  };

  const combinedClassName = [
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component className={combinedClassName} ref={ref} {...rest}>
      {leftIcon && <span className={`mr-2 flex items-center justify-center ${iconSizeStyles[size]}`}>{leftIcon}</span>}
      {children}
      {rightIcon && <span className={`ml-2 flex items-center justify-center ${iconSizeStyles[size]}`}>{rightIcon}</span>}
    </Component>
  );
}

// --- Forward Ref ---
const ButtonWithRef = React.forwardRef(ButtonBase);

// --- Final Polymorphic Type Assertion ---
const Button = ButtonWithRef as <C extends React.ElementType = 'button'>(
  props: ButtonProps<C> & { ref?: PolymorphicRef<C> }
) => React.ReactElement | null;

export { Button };
